'use client'

import { CaseManagementIcon } from '@repo/ui/CaseManagementIcon'
import { CircledCheckIcon } from '@repo/ui/CircledCheckIcon'
import { CircledCrossIcon } from '@repo/ui/CircledCrossIcon'
import { ClientPortalIcon } from '@repo/ui/ClientPortalIcon'
import { Link } from 'react-router-dom'
import { LinkIcon } from '@repo/ui/LinkIcon'
import { styled } from '@mui/material/styles'
import { SxProps } from '@mui/material'
import { useClientPortalStore } from '@repo/store/useClientPortalStore'
import { useEffect } from 'react'
import { useMattersStore } from '@repo/store/useMattersStore'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import styles from './ClientPortalCard.module.scss'

export const ClientPortalCard = ({ testData, sx }: ClientPortalCardProps) => {
  const { currentMatter } = useMattersStore()
  const { clientPortalData, fetchClientPortalData } = useClientPortalStore()
  const { clientNo } = currentMatter || {}

  useEffect(() => {
    clientNo && fetchClientPortalData({ clientNo })
  }, [clientNo])

  const countAssociateIds = (matterAssociates: any[]) => {
    const counts: Record<string, number> = {}

    matterAssociates.forEach((associate) => {
      const id = associate.clientPortalAssociateId
      if (id) {
        counts[id] = (counts[id] || 0) + 1
      }
    })

    return counts
  }

  const associateCounts =
    !testData && clientPortalData.matterAssociates
      ? countAssociateIds(clientPortalData.matterAssociates)
      : testData && testData.matterAssociates
        ? countAssociateIds(testData.matterAssociates)
        : {}

  const uniqueAssociates =
    !testData && clientPortalData.matterAssociates
      ? Array.from(
          // @ts-ignore
          new Map(
            clientPortalData.matterAssociates.map((associate: any) => [associate.clientPortalAssociateId, associate])
          ).values()
        )
      : !clientPortalData && testData && testData.matterAssociates
        ? Array.from(
            // @ts-ignore
            new Map(
              clientPortalData.matterAssociates.map((associate: any) => [associate.clientPortalAssociateId, associate])
            ).values()
          )
        : []

  const PillTooltip = styled(({ className, ...props }: TooltipProps) => (
    <MUITooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: '#051C33',
      borderRadius: '4px',
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
      color: '#fff',
      fontSize: '0.875rem',
      lineHeight: '1.4',
      maxWidth: '200px',
      padding: '0.5rem 0.75rem 0.35rem',
      left: '0',
      bottom: '0',
      position: 'relative',
      cursor: 'pointer',
      [`&:before`]: {
        content: '""',
        width: '0',
        height: '0',
        borderLeft: '5px solid transparent',
        borderRight: '5px solid transparent',
        borderBottom: '5px solid #051C33',
        position: 'absolute',
        top: '-4px',
        left: '50%'
      }
    }
  }))

  return (
    <MUIPaper
      data-testid="ClientPortalCard"
      sx={{
        width: '100%',
        height: 'auto',
        padding: '0',
        background: 'white',
        borderRadius: 1,
        boxShadow: 'none',
        border: '1px solid #D8DCE5',
        position: 'relative',
        minHeight: '100%',
        ...sx
      }}
    >
      <MUIStack display="flex" direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
        <MUITypography
          sx={{
            padding: '1rem 1rem 0',
            margin: '0'
          }}
          variant="h6"
        >
          Client portal
        </MUITypography>

        <MUITypography
          className={`${styles.enablePill} ${!clientPortalData.clientPortalEnabled ? styles.disabled : ''}`}
        >
          {clientPortalData.clientPortalEnabled ? 'Enabled' : 'Not enabled'}
        </MUITypography>
      </MUIStack>

      <MUIStack display="flex" direction="row" sx={{ margin: '0.75rem 1rem' }} gap={4}>
        {clientPortalData.clientPortalEnabled ? (
          <>
            <MUIPaper
              sx={{
                width: '100%',
                height: 'auto',
                padding: '0 0 0.5rem',
                background: 'white',
                borderRadius: 1,
                boxShadow: 'none',
                border: '1px solid #D8DCE5',
                position: 'relative',
                minHeight: '100%',
                overflow: 'hidden'
              }}
            >
              <Link to="/case-management/clients/client-portal/enable-client-portal" className={styles.link}>
                <MUITypography
                  sx={{
                    cursor: 'pointer',
                    padding: '1rem 1rem 0.5rem',
                    margin: '0'
                  }}
                  variant="h6"
                >
                  Clients
                </MUITypography>
                <LinkIcon sx={{ width: '14px', height: '14px', top: '19px', right: '-4px', position: 'absolute' }} />
              </Link>
              <MUIStack display="block" sx={{ maxHeight: '9rem', overflowY: 'auto' }}>
                <MUIStack display="flex" direction="column">
                  {!testData && clientPortalData.namedClients ? (
                    clientPortalData.namedClients.map((client: any) => (
                      <MUIStack
                        key={client.clientName}
                        display="grid"
                        sx={{
                          gridTemplateColumns: '10% 80% 10%',
                          gridTemplateRows: 'resolvePath(2, 5rem)',
                          justifyContent: 'space-around',
                          alignItems: 'center',
                          margin: '0.5rem 0'
                        }}
                      >
                        {client.hasClientPortalAccess ? (
                          <CircledCheckIcon
                            sx={{ width: '18px', height: '18px', gridArea: '1/1/3/2', justifySelf: 'center' }}
                          />
                        ) : (
                          <CircledCrossIcon
                            sx={{ width: '18px', height: '18px', gridArea: '1/1/3/2', justifySelf: 'center' }}
                          />
                        )}
                        <MUITypography variant="body1" sx={{ mb: '0', gridArea: '1/2/2/3' }}>
                          {client.clientName}
                        </MUITypography>
                        <MUITypography variant="body2" sx={{ mb: '0', gridArea: '2/2/3/3' }}>
                          {client.email}
                        </MUITypography>
                        <MUITypography
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gridArea: '1/3/3/4',
                            justifySelf: 'flex-start',
                            backgroundColor: '#ECEEF2',
                            borderRadius: '16px',
                            padding: '4px 8px 2px',
                            position: 'relative',
                            right: '2rem'
                          }}
                        >
                          All{' '}
                          <CaseManagementIcon
                            sx={{ height: '1.25rem', width: '1.25rem', marginBottom: '0.25rem', marginLeft: '0.5rem' }}
                          />
                        </MUITypography>
                      </MUIStack>
                    ))
                  ) : testData && !clientPortalData.namedClients ? (
                    testData.namedClients.map((test: any) => (
                      <MUIStack
                        key={test.clientName}
                        display="grid"
                        sx={{
                          gridTemplateColumns: '10% 80% 10%',
                          gridTemplateRows: 'resolvePath(2, 5rem)',
                          justifyContent: 'space-around',
                          alignItems: 'center',
                          margin: '0.5rem 0'
                        }}
                      >
                        {test.hasClientPortalAccess ? (
                          <CircledCheckIcon
                            sx={{ width: '18px', height: '18px', gridArea: '1/1/3/2', justifySelf: 'center' }}
                          />
                        ) : (
                          <CircledCrossIcon
                            sx={{ width: '18px', height: '18px', gridArea: '1/1/3/2', justifySelf: 'center' }}
                          />
                        )}
                        <MUITypography variant="body1" sx={{ mb: '0', gridArea: '1/2/2/3' }}>
                          {test.clientName}
                        </MUITypography>
                        <MUITypography variant="body2" sx={{ mb: '0', gridArea: '2/2/3/3' }}>
                          {test.email}
                        </MUITypography>
                        <MUITypography
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gridArea: '1/3/3/4',
                            justifySelf: 'flex-start',
                            backgroundColor: '#ECEEF2',
                            borderRadius: '16px',
                            padding: '4px 8px 2px',
                            position: 'relative',
                            right: '2rem'
                          }}
                        >
                          All{' '}
                          <CaseManagementIcon
                            sx={{ height: '1.25rem', width: '1.25rem', marginBottom: '0.25rem', marginLeft: '0.5rem' }}
                          />
                        </MUITypography>
                      </MUIStack>
                    ))
                  ) : (
                    <MUITypography
                      sx={{
                        padding: '1rem'
                      }}
                      variant="h6"
                    >
                      No clients found
                    </MUITypography>
                  )}
                </MUIStack>
              </MUIStack>
            </MUIPaper>
            <MUIPaper
              sx={{
                width: '100%',
                height: 'auto',
                padding: '0',
                background: 'white',
                borderRadius: 1,
                boxShadow: 'none',
                border: '1px solid #D8DCE5',
                position: 'relative',
                minHeight: '100%',
                overflow: 'hidden'
              }}
            >
              <Link to="/case-management/clients/client-portal/enable-associate-portal" className={styles.link}>
                <MUITypography
                  sx={{
                    cursor: 'pointer',
                    padding: '1rem 1rem 0.5rem',
                    margin: '0'
                  }}
                  variant="h6"
                >
                  Associates
                </MUITypography>
                <LinkIcon sx={{ width: '14px', height: '14px', top: '19px', right: '-4px', position: 'absolute' }} />
              </Link>
              <MUIStack display="block" sx={{ maxHeight: '9rem', overflowY: 'auto' }}>
                <MUIStack display="flex" direction="column">
                  {uniqueAssociates.length > 0 ? (
                    uniqueAssociates.map((associate: any) => (
                      <MUIStack
                        key={associate.clientPortalAssociateId}
                        display="grid"
                        sx={{
                          gridTemplateColumns: '10% 80% 10%',
                          gridTemplateRows: 'resolvePath(2, 5rem)',
                          justifyContent: 'space-around',
                          alignItems: 'center',
                          margin: '0.5rem 0'
                        }}
                      >
                        <CircledCheckIcon
                          sx={{ width: '18px', height: '18px', gridArea: '1/1/3/2', justifySelf: 'center' }}
                        />
                        <MUITypography variant="body1" sx={{ mb: '0', gridArea: '1/2/2/3' }}>
                          {associate.associateName}
                        </MUITypography>
                        <MUITypography variant="body2" sx={{ mb: '0', gridArea: '2/2/3/3' }}>
                          {associate.email}
                        </MUITypography>
                        <PillTooltip
                          className={styles.tooltip}
                          disableFocusListener
                          title={`Has access to ${associateCounts[associate.clientPortalAssociateId] ?? 0} matter${associateCounts[associate.clientPortalAssociateId] > 1 ? 's' : ''}`}
                          placement="bottom"
                        >
                          <MUITypography
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              gridArea: '1/3/3/4',
                              justifySelf: 'flex-start',
                              backgroundColor: '#ECEEF2',
                              borderRadius: '16px',
                              padding: '4px 8px 2px',
                              position: 'relative',
                              right: '2rem',
                              cursor: 'pointer'
                            }}
                          >
                            {associateCounts[associate.clientPortalAssociateId] ?? 0}{' '}
                            <CaseManagementIcon
                              sx={{
                                height: '1.25rem',
                                width: '1.25rem',
                                marginBottom: '0.25rem',
                                marginLeft: '0.5rem'
                              }}
                            />
                          </MUITypography>
                        </PillTooltip>
                      </MUIStack>
                    ))
                  ) : (
                    <MUITypography
                      sx={{
                        padding: '1rem'
                      }}
                      variant="h6"
                    >
                      No associates found
                    </MUITypography>
                  )}
                </MUIStack>
              </MUIStack>
            </MUIPaper>
          </>
        ) : (
          <MUIStack
            display="flex"
            direction="column"
            sx={{ alignItems: 'center', justifyContent: 'center', mt: '2rem', width: '100%' }}
          >
            <ClientPortalIcon sx={{ height: '60px', width: '60px' }} />
            <Link to="/case-management/clients/client-portal/enable-client-portal">
              <MUITypography
                sx={{
                  padding: '1rem',
                  cursor: 'pointer'
                }}
                variant="body1"
              >
                View client portal settings
              </MUITypography>
            </Link>
          </MUIStack>
        )}
      </MUIStack>
    </MUIPaper>
  )
}

ClientPortalCard.propTypes = {
  sx: PropTypes.object,
  testData: PropTypes.object
}

export type ClientPortalCardProps = {
  sx?: SxProps
  testData?: any
}
